import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import {
  AppealSectionForLp,
  CompanyCaseDetail,
  CompanyCaseForDetail,
  FooterForLp,
  HeaderForLp,
} from 'components/pages/lp'
import { NextHead } from 'components/shared/NextHead'
import { Entry, EntryCollection } from 'contentful'
import { buildClient, getCredentialsFromEnv, IArticle } from 'libs/contentful'
import { InferGetStaticPropsType, NextPage } from 'next'

import { locales } from '../../../../../constants/locales'

export async function getStaticPaths() {
  const { space, accessToken, environment } = getCredentialsFromEnv()

  if (!space || !accessToken) {
    return { paths: [], fallback: false }
  }

  const client = buildClient({ space, accessToken, environment })

  const localeList = Object.values(locales)

  const { items: articles }: EntryCollection<IArticle> =
    await client.getEntries({
      content_type: 'company_case',
    })
  return {
    paths: articles
      .map((article) => {
        return localeList.map((locale) => {
          return { params: { id: article.sys.id }, locale }
        })
      })
      .flat(),
    fallback: false,
  }
}

// `getStaticPaths` requires using `getStaticProps`
export async function getStaticProps(context: { params: { id: string } }) {
  const { space, accessToken, environment } = getCredentialsFromEnv()

  if (!space || !accessToken) {
    return { notFound: true }
  }
  const client = buildClient({ space, accessToken, environment })

  const { items: articles }: EntryCollection<IArticle> =
    await client.getEntries({
      content_type: 'company_case',
      limit: 3,
    })

  const article: Entry<IArticle> = await client.getEntry<IArticle>(
    context.params.id,
  )

  if (!article) {
    return { notFound: true }
  }

  return {
    props: { article, articles },
  }
}

type Props = InferGetStaticPropsType<typeof getStaticProps>

const Page: NextPage<Props> & {
  getHead: (pageProps: { article: Entry<IArticle> }) => JSX.Element
} = ({ article, articles }) => {
  return (
    <>
      <HeaderForLp />
      <CompanyCaseDetail article={article} />
      <CompanyCaseForDetail articles={articles} />
      <AppealSectionForLp />
      <FooterForLp />
    </>
  )
}

Page.getHead = (pageProps: { article: Entry<IArticle> }) => {
  const { article } = pageProps

  return (
    <NextHead
      title={`[${article.fields.companyName} | ${article.fields.representativeName}]${article.fields.title} - ArtXCloud`}
      // NOTE: descriptionは、本文の先頭100文字を使用
      description={`${documentToPlainTextString(
        article.fields.contents,
      ).substring(0, 100)}...`}
      thumbnailUrl={article.fields.thumbnail.fields.file.url}
    />
  )
}

export default Page
